import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { MyExpRoute } from "./types";
import AppWrapper from "./appWrapper";
import lazyLoad from "shared/utils/lazyLoad";
import ManageOrgAdminModal from "../modules/manageOrgAdmin/list";
import CreateUpdateOrgAdmin from "../modules/manageOrgAdmin/createUpdate";
import HandleMyExperienceRedirects from "../components/handleRedirect";
import UploadProfilePictureHandler from "../components/profileImageUpload/profileImageUploadHandler";
import SocialMediaShareHandler from "../components/socialMediaShareHandler";
import ManageBulkTicketRoute from "../modules/bulkTicketManagement/components/manageBulkTicketRoute";
import PurchaseHistory from "../components/purchaseHistory";
import PublicRoutesWrapper from "./publicRoutesWrapper";
import AdditionalCharge from "../modules/additionalCharge";
import ConsentBannerContainer from "../../../shared/consentManagement";
import { ConsentProvider } from "../../../context/consent";
import PurchaseTicketWrapper from "../modules/purchaseTicket/purchaseTicketWrapper";
import AssignTicketWrapper from "../modules/bulkTicketManagement/components/assignTicket/assignTicketWrapper";
import ManageParticipantWrapper from "../modules/manageParticipant/manageParticipantWrapper";
import MoreTimeslots from "shared/components/activity/list/components/moreTimeslots";
import { ActivityListForParticipants } from "../modules/profile/associated";
import { AssociatedProgramWorkFlow } from "../modules/workflow";
import TicketChange from "../modules/ticketChange";
import ApplicableFormsList from "../modules/ticketChange/applicableFormsList";
import BuyTicketFormDetails from "../modules/ticketChange/buyTicketFormDetails";
import CreateUpdateContextLocationForMyExperience from "../modules/contactContext/details";
import MyExpPaymentStatus from "../modules/paymentStatus";

const Dashboard = lazyLoad(() => import("../modules/dashboard"));
const MyInfo = lazyLoad(() => import("../modules/myinfo"));
const Profile = lazyLoad(() => import("../modules/profile"));
const OrganizationProfile = lazyLoad(() => import("../modules/orgProfile"));
const EventOrganizationProfile = lazyLoad(() => import("../modules/eventOrganizationProfile"));
const Organization = lazyLoad(() => import("../modules/organization"));
const AcceptMeetings = lazyLoad(() => import("../modules/acceptMeetings"));
const OrganizationParticipantProfileListing = lazyLoad(() => import("../modules/organization/profile/list"));
const OrganizationParticipantProfile = lazyLoad(() => import("../modules/organization/profile/details"));
const TicketTransfer = lazyLoad(() => import("../modules/dashboard/ticketTransfer"));
const Agenda = lazyLoad(() => import("../modules/agenda"));
const Login = lazyLoad(() => import("../modules/login"));
const ForgotPassword = lazyLoad(() => import("../modules/forgotPassword"));
const FAQ = lazyLoad(() => import("../modules/faq"));
const MeetingSelection = lazyLoad(() => import("../modules/selection"));
const ExhibitorsList = lazyLoad(() => import("../modules/exhibitorsList"));
const MyExperienceLeads = lazyLoad(() => import("../modules/contactContext/list"));
const OrgAdminSelectionParticipantList = lazyLoad(() => import("../modules/selection/orgAdmin"));
const ParticipantSchedule = lazyLoad(() => import("../modules/participantSchedule"));
const OrganizationSchedule = lazyLoad(() => import("../modules/organizationSchedule"));
const ParticipantScheduleMeetingDetails = lazyLoad(() => import("../modules/participantSchedule/details"));
const OrganizationAgendaParticipationListing = lazyLoad(() => import("../modules/organization/agenda"));
const OrganizationAcceptMeetings = lazyLoad(() => import("../modules/organization/agenda/components/acceptMeetings"));
const OrgScheduleWrapper = lazyLoad(() => import("../modules/organization/agenda/components/orgScheduleWrapper"));
const AgendaDefaultInstructionsView = lazyLoad(() => import("../modules/participantSchedule/defaultDetailsView"));
const ReimbursementHome = lazyLoad(() => import("../modules/reimbursement"));
const Feedback = lazyLoad(() => import("../modules/feedback"));
const FeedbackList = lazyLoad(() => import("../modules/organization/feedback"));
const RegistrationElapsed = lazyLoad(() => import("shared/components/registrationElapsed"));
const AgendaParticipationInvite = lazyLoad(() => import("../modules/agendaParticipationInvite"));
const Contacts = lazyLoad(() => import("../modules/contacts"));
const MagicLoginRoute = lazyLoad(() => import("shared/components/magicLogin"));

export default function MyExperienceRouter() {
  return (
    <Router>
      <ConsentProvider>
        <Routes>
          <Route path={MyExpRoute.HOME.path} element={<AppWrapper />}>
            <Route index element={<Dashboard />} />
            <Route path={`${MyExpRoute.HOME.path}/*`} element={<Dashboard />}>
              <Route path={MyExpRoute.UPDATE_PROFILE_PIC.path} element={<UploadProfilePictureHandler />} />
              <Route path={MyExpRoute.SOCIAL_MEDIA_SHARE.path} element={<SocialMediaShareHandler />} />
              <Route path={MyExpRoute.MY_INFO.path} element={<MyInfo />} />
              <Route path={MyExpRoute.TICKET_TRANSFER.path} element={<TicketTransfer />} />
              <Route path={MyExpRoute.MANAGE_ORG_ADMIN.path} element={<ManageOrgAdminModal />}>
                <Route path={MyExpRoute.MANAGE_ORG_ADMIN.create.path} element={<CreateUpdateOrgAdmin />} />
                <Route path={MyExpRoute.MANAGE_ORG_ADMIN.assign.path} element={<CreateUpdateOrgAdmin />} />
              </Route>
              <Route path={MyExpRoute.MANAGE_BULK_TICKET.path} element={<ManageBulkTicketRoute />} />
              <Route path={MyExpRoute.PURCHASE_HISTORY.path} element={<PurchaseHistory />} />
              <Route path={MyExpRoute.ADDITIONAL_CHARGE.path} element={<AdditionalCharge />} />
              <Route path={MyExpRoute.TICKET_PURCHASE.path} element={<PurchaseTicketWrapper />} />
              <Route path={MyExpRoute.TICKET_ASSIGN.path} element={<AssignTicketWrapper />} />
              <Route path={MyExpRoute.MANAGE_PARTICIPANT.path} element={<ManageParticipantWrapper />} />
              <Route path={MyExpRoute.TICKET_CHANGE.path} element={<TicketChange />}>
                <Route path={MyExpRoute.TICKET_CHANGE.FORMS_LIST.path} element={<ApplicableFormsList />} />
                <Route path={MyExpRoute.TICKET_CHANGE.FORM_DETAILS.path} element={<BuyTicketFormDetails />} />
              </Route>
            </Route>
            <Route path={MyExpRoute.SELECTION.path} element={<MeetingSelection />} />
            <Route path={MyExpRoute.EXHIBITORS_LIST.path} element={<ExhibitorsList />} />
            <Route path={MyExpRoute.SPONSORS_LIST.path} element={<ExhibitorsList showSponsors={true} />} />
            <Route path={MyExpRoute.LEADS.path} element={<MyExperienceLeads />}>
              <Route
                path={MyExpRoute.LEADS.LEAD_LOCATION_DETAILS.path}
                element={<CreateUpdateContextLocationForMyExperience />}
              />
            </Route>
            <Route path={MyExpRoute.LEADS_PARTICIPANT.path} element={<Contacts />} />

            <Route path={MyExpRoute.REIMBURSEMENT.path} element={<ReimbursementHome />} />
            <Route path={MyExpRoute.ORG_ADMIN_SELECTION.path} element={<OrgAdminSelectionParticipantList />} />
            <Route path={MyExpRoute.AGENDA.path} element={<Agenda />} />
            <Route path={MyExpRoute.PROFILE.path} element={<Profile isTabletalk={false} />} />
            <Route path={MyExpRoute.TABLETALK_PROFILE.path} element={<Profile isTabletalk={true} />} />
            <Route path={`${MyExpRoute.WORKFLOW.path}/*`} element={<AssociatedProgramWorkFlow />} />
            <Route path={MyExpRoute.ASSOCIATED_PROGRAM_ACTIVITY_LIST.path} element={<ActivityListForParticipants />}>
              <Route path={MyExpRoute.ASSOCIATED_PROGRAM_ACTIVITY_LIST.TIMESLOTS.path} element={<MoreTimeslots />} />
            </Route>
            <Route path={MyExpRoute.ORGANIZATION_PROFILE.path} element={<OrganizationProfile />} />
            <Route path={MyExpRoute.EVENT_ORGANIZATION_PROFILE.path} element={<EventOrganizationProfile />} />
            <Route path={MyExpRoute.ACCEPT_MEETINGS.path} element={<AcceptMeetings />} />
            <Route path={MyExpRoute.ASSIGN_MEETING_REP.path} element={<AcceptMeetings />} />
            <Route path={MyExpRoute.ORGANIZATION.path} element={<Organization />}>
              <Route
                path={MyExpRoute.ORGANIZATION.PROFILE_LIST.path}
                element={<OrganizationParticipantProfileListing />}>
                <Route path={":programParticipantId"} element={<OrganizationParticipantProfile />} />
              </Route>
              <Route path={MyExpRoute.ORGANIZATION.PROFILE.path} element={<OrganizationParticipantProfile />} />
              <Route
                path={MyExpRoute.ORGANIZATION.AGENDA_PARTICIPATION_LIST.path}
                element={<OrganizationAgendaParticipationListing />}
              />
              <Route path={MyExpRoute.ORGANIZATION.ACCEPT_MEETINGS.path} element={<OrganizationAcceptMeetings />} />
              <Route path={MyExpRoute.ORGANIZATION.ORG_SCHEDULE.path} element={<OrgScheduleWrapper />}>
                <Route
                  path={MyExpRoute.ORGANIZATION.ORG_SCHEDULE.PARTICIPANT_SCHEDULE.path}
                  element={<ParticipantSchedule />}>
                  <Route
                    path={MyExpRoute.ORGANIZATION.ORG_SCHEDULE.PARTICIPANT_SCHEDULE.MEETING_DETAIL.path}
                    element={<ParticipantScheduleMeetingDetails />}
                  />
                  <Route
                    path={MyExpRoute.ORGANIZATION.ORG_SCHEDULE.PARTICIPANT_SCHEDULE.MEETING_DETAIL_DEFAULT.path}
                    element={<AgendaDefaultInstructionsView />}
                  />
                </Route>
              </Route>
              <Route path={MyExpRoute.ORGANIZATION.FEEDBACK_LIST.path} element={<FeedbackList />} />
            </Route>
            <Route path={MyExpRoute.ORGANIZATION_SCHEDULE.path} element={<OrganizationSchedule />} />
            <Route path={MyExpRoute.PARTICIPANT_SCHEDULE.path} element={<ParticipantSchedule />}>
              <Route
                path={MyExpRoute.PARTICIPANT_SCHEDULE.MEETING_DETAIL.path}
                element={<ParticipantScheduleMeetingDetails />}
              />
              <Route
                path={MyExpRoute.PARTICIPANT_SCHEDULE.MEETING_DETAIL_DEFAULT.path}
                element={<AgendaDefaultInstructionsView />}
              />
            </Route>
            <Route path={MyExpRoute.FEEDBACK.path} element={<Feedback />} />
            <Route path={MyExpRoute.AGENDA_PARTICIPATION_INVITE.path} element={<AgendaParticipationInvite />} />
            <Route path={MyExpRoute.CONTACTS.path} element={<Contacts />} />
            <Route path={MyExpRoute.PAYMENT_STATUS.path} element={<MyExpPaymentStatus />} />
          </Route>
          <Route element={<PublicRoutesWrapper />}>
            <Route path={MyExpRoute.LOGIN.path} element={<Login />} />
            <Route path={MyExpRoute.FORGOT_PASSWORD.path} element={<ForgotPassword />} />
            <Route path={MyExpRoute.FAQ.path} element={<FAQ />} />
            <Route path={MyExpRoute.REGISTRATION_ELAPSED.path} element={<RegistrationElapsed />} />
            <Route path={MyExpRoute.MAGIC_LOGIN.path} element={<MagicLoginRoute />} />
          </Route>
          <Route path={MyExpRoute.REDIRECT.path} element={<HandleMyExperienceRedirects />} />
        </Routes>
        <ConsentBannerContainer />
      </ConsentProvider>
    </Router>
  );
}
